import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { GraduationCap, Heart, Users, Star } from 'lucide-react';

export const AboutJanaSection = () => {
  const qualifications = [
    { icon: GraduationCap, text: 'Staatlich anerkannte Logopädin' },
    { icon: Heart, text: 'Praxisinhaberin seit Gründung' },
    { icon: Users, text: 'Therapie für alle Altersgruppen' },
    { icon: Star, text: 'Individuelle Therapiekonzepte' },
  ];

  return (
    <section id="ueber-mich" className="section-padding bg-background relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="about-pattern" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1" fill="hsl(var(--primary))" />
            </pattern>
          </defs>
          <rect x="0" y="0" width="100" height="100" fill="url(#about-pattern)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto container-padding relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-primary text-sm font-body font-medium tracking-wide uppercase mb-4">
            Ihre Therapeutin
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-heading mb-6">
            Über mich
          </h2>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
          {/* Left: Portrait Area */}
          <div className="lg:col-span-2">
            <div className="relative">
              {/* Portrait placeholder */}
              <div className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-gradient-to-br from-primary-light via-secondary-light to-accent-warm shadow-elegant">
                {/* Decorative overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-heading/20 via-transparent to-transparent" />
                
                {/* Placeholder content */}
                <div className="absolute inset-0 flex items-end justify-center pb-8">
                  <div className="text-center">
                    <div className="w-32 h-32 bg-card/90 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg border-4 border-card">
                      <span className="font-heading text-4xl text-primary font-semibold">JP</span>
                    </div>
                    <p className="text-heading font-heading text-xl font-medium bg-card/80 backdrop-blur-sm px-4 py-2 rounded-full">
                      Jana Prümer
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 lg:-right-8 bg-card rounded-2xl shadow-card p-4 border border-border">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                    <Star className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-heading text-sm font-semibold">Praxisinhaberin</p>
                    <p className="text-caption text-xs">& Therapeutin</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Qualifications */}
            <div className="mt-12 space-y-4">
              {qualifications.map((qual, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors duration-300"
                >
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <qual.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-body font-body text-sm">{qual.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Bio Content */}
          <div className="lg:col-span-3 space-y-8">
            <div>
              <h3 className="font-heading text-2xl lg:text-3xl font-medium text-heading mb-4">
                Jana Prümer
              </h3>
              <p className="text-primary font-body font-medium text-lg mb-6">
                Staatlich anerkannte Logopädin & Praxisinhaberin
              </p>
            </div>

            <div className="space-y-5 text-body font-body leading-relaxed">
              <p>
                Als staatlich anerkannte Logopädin habe ich meine Praxis in Schöppingen 
                mit dem Wunsch gegründet, Menschen jeden Alters bei ihren kommunikativen 
                Herausforderungen zu unterstützen.
              </p>
              <p>
                Meine Arbeit basiert auf der Überzeugung, dass jeder Mensch einzigartig ist 
                und eine individuell abgestimmte Therapie verdient. Ich nehme mir die Zeit, 
                Sie und Ihre Bedürfnisse kennenzulernen, um gemeinsam den bestmöglichen 
                Therapieweg zu finden.
              </p>
              <p>
                In meiner Praxis behandle ich Kinder, Jugendliche und Erwachsene mit 
                verschiedenen Störungsbildern. Dabei ist mir eine vertrauensvolle 
                Atmosphäre besonders wichtig – nur so kann eine erfolgreiche Therapie 
                gelingen.
              </p>
              <p>
                Die enge Zusammenarbeit mit Angehörigen, Ärzten und anderen Therapeuten 
                ist für mich selbstverständlich, denn nur gemeinsam können wir die besten 
                Ergebnisse erzielen.
              </p>
            </div>

            {/* Quote Card */}
            <Card className="bg-primary/5 border-primary/10 mt-8">
              <CardContent className="p-6 lg:p-8">
                <blockquote className="relative">
                  <div className="absolute -top-2 -left-2 text-primary/20 text-6xl font-heading">&ldquo;</div>
                  <p className="text-body font-body text-lg italic relative z-10 pl-6">
                    Jeder Mensch hat das Recht, gehört und verstanden zu werden. 
                    Es ist mir eine Herzensangelegenheit, meinen Patienten dabei zu helfen, 
                    ihre Stimme zu finden und ihre Kommunikationsfähigkeiten zu verbessern.
                  </p>
                  <footer className="mt-4 pl-6">
                    <cite className="text-primary font-semibold not-italic">Jana Prümer</cite>
                  </footer>
                </blockquote>
              </CardContent>
            </Card>

            {/* Personal approach highlights */}
            <div className="grid sm:grid-cols-2 gap-4 mt-8">
              <div className="bg-muted/50 rounded-2xl p-5">
                <h4 className="font-heading text-lg font-medium text-heading mb-2">
                  Mein Ansatz
                </h4>
                <p className="text-body font-body text-sm">
                  Patientenzentriert, einfühlsam und auf dem neuesten Stand der 
                  logopädischen Forschung.
                </p>
              </div>
              <div className="bg-muted/50 rounded-2xl p-5">
                <h4 className="font-heading text-lg font-medium text-heading mb-2">
                  Meine Motivation
                </h4>
                <p className="text-body font-body text-sm">
                  Jeden Tag erlebe ich, wie kleine Fortschritte große Veränderungen 
                  im Leben meiner Patienten bewirken.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutJanaSection;
